import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Checkbox } from '@heroui/react';
import { useResetMailboxPassword } from '@/features/mailboxes/hooks';
import { resetMailboxPwSchema, type ResetMailboxPwValues } from '@/features/mailboxes/schema';
import type { Mailbox } from '@/features/mailboxes/types';

export function MailboxResetPasswordModal({ isOpen, onClose, mailbox }: { isOpen: boolean; onClose: () => void; mailbox: Mailbox }) {
  const reset = useResetMailboxPassword(mailbox.id);
  const form = useForm<ResetMailboxPwValues>({ resolver: zodResolver(resetMailboxPwSchema), defaultValues: { newPassword: '', revokeSessions: true } });
  const onSubmit = form.handleSubmit(async (v) => { await reset.mutateAsync(v); onClose(); form.reset(); });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent>
        <ModalHeader className="text-fg">Reset password</ModalHeader>
        <ModalBody>
          <p className="text-sm text-fg-muted">Set a new password for <span className="font-medium text-fg">{mailbox.address}</span>.</p>
          <form id="mb-reset" onSubmit={onSubmit} className="mt-2 space-y-4" noValidate>
            <Input {...form.register('newPassword')} type="password" label="New password" variant="bordered" autoComplete="new-password"
              isInvalid={!!form.formState.errors.newPassword} errorMessage={form.formState.errors.newPassword?.message} />
            <Checkbox isSelected={form.watch('revokeSessions')} onValueChange={(v) => form.setValue('revokeSessions', v)}>
              Sign out all active sessions
            </Checkbox>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button color="primary" type="submit" form="mb-reset" isLoading={reset.isPending}>Reset password</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
